import { LuIcon, MenuV2 } from "@overmap-ai/blocks"
import { type ChainedCommands, Editor } from "@tiptap/core"
import { EditorState } from "@tiptap/pm/state"
import { EditorView } from "@tiptap/pm/view"
import { type ChangeEventHandler, type ComponentProps, memo, useCallback } from "react"

import { useFileInputRef } from "../../../hooks/utils"
import { modelIconMapping } from "../../../mappings/icons"
import { FloatingMenu } from "../../blocks"
import { HEADING_LEVELS } from "../../constants"
import { HeadingLevel } from "../../enums"
import { useFallbackEditor } from "../../hooks"
import { tipTapIconMapping } from "../mappings"
import { HeadingMenuGroup } from "../MenuGroups"

const SHOW_CHARACTER = "/"

interface NewLineFloatingMenuProps extends ComponentProps<typeof FloatingMenu> {
	onAttachFiles?: (commandChain: () => ChainedCommands, files: File[]) => void
}

export const NewLineFloatingMenu = memo((props: NewLineFloatingMenuProps) => {
	const { editor: editorFromProps, onAttachFiles, ...rest } = props

	const editor = useFallbackEditor(editorFromProps)

	const [fileInputRef, triggerFileInput] = useFileInputRef()

	const commonCommandChain = useCallback(() => {
		const { from } = editor.state.selection
		return editor
			.chain()
			.focus()
			.deleteRange({
				from: Math.max(0, from - SHOW_CHARACTER.length),
				to: from,
			})
	}, [editor])

	const handleHeadingSelect = useCallback(
		(level: HeadingLevel) => {
			commonCommandChain().setHeading({ level }).run()
		},
		[commonCommandChain],
	)

	const handleBulletListSelect = useCallback(() => {
		commonCommandChain().toggleBulletList().run()
	}, [commonCommandChain])

	const handleOrderedListSelect = useCallback(() => {
		commonCommandChain().toggleOrderedList().run()
	}, [commonCommandChain])

	const handleTaskListSelect = useCallback(() => {
		commonCommandChain().toggleTaskList().run()
	}, [commonCommandChain])

	const handleCodeBlockSelect = useCallback(() => {
		commonCommandChain().setCodeBlock().run()
	}, [commonCommandChain])

	const handleBlockqouteSelect = useCallback(() => {
		commonCommandChain().setBlockquote().run()
	}, [commonCommandChain])

	const handleSeparatorSelect = useCallback(() => {
		commonCommandChain().setHorizontalRule().run()
	}, [commonCommandChain])

	const handleFileInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
		(e) => {
			const files = Array.from(e.target.files ?? [])
			if (files.length === 0) return
			onAttachFiles?.(commonCommandChain, files)
			if (fileInputRef.current) fileInputRef.current.value = ""
		},
		[commonCommandChain, fileInputRef, onAttachFiles],
	)

	return (
		<>
			<FloatingMenu editor={editor} shouldShow={shouldShow} {...rest}>
				<MenuV2.Root className="relative" size="sm">
					<HeadingMenuGroup levels={HEADING_LEVELS} onSelect={handleHeadingSelect} />
					<MenuV2.Separator />
					<MenuV2.Item onSelect={handleBulletListSelect}>
						<LuIcon icon={tipTapIconMapping.bulletList} />
						Bullet list
					</MenuV2.Item>
					<MenuV2.Item onSelect={handleOrderedListSelect}>
						<LuIcon icon={tipTapIconMapping.orderedList} />
						Ordered list
					</MenuV2.Item>
					<MenuV2.Item onSelect={handleTaskListSelect}>
						<LuIcon icon={tipTapIconMapping.taskList} />
						Task list
					</MenuV2.Item>
					<MenuV2.Separator />
					<MenuV2.Item onSelect={handleCodeBlockSelect}>
						<LuIcon icon={tipTapIconMapping.codeBlock} />
						Code block
					</MenuV2.Item>
					<MenuV2.Item onSelect={handleSeparatorSelect}>
						<LuIcon icon={tipTapIconMapping.horizontalRule} />
						Separator
					</MenuV2.Item>
					<MenuV2.Item onSelect={handleBlockqouteSelect}>
						<LuIcon icon={tipTapIconMapping.blockqoute} />
						Blockquote
					</MenuV2.Item>
					{!!onAttachFiles && (
						<MenuV2.Item onSelect={triggerFileInput}>
							<LuIcon icon={modelIconMapping.attachment} />
							Attach files…
						</MenuV2.Item>
					)}
				</MenuV2.Root>
			</FloatingMenu>

			<input className="hidden" type="file" ref={fileInputRef} multiple onChange={handleFileInputChange} />
		</>
	)
})
NewLineFloatingMenu.displayName = "FloatingMenu"

function shouldShow({ view, state, editor }: { editor: Editor; view: EditorView; state: EditorState }) {
	const { selection } = state
	const { $anchor, empty } = selection
	const isRootDepth = $anchor.depth === 1
	const isEmptyTextBlock =
		$anchor.parent.isTextblock && !$anchor.parent.type.spec.code && $anchor.parent.textContent === SHOW_CHARACTER

	return !(!view.hasFocus() || !empty || !isRootDepth || !isEmptyTextBlock || !editor.isEditable)
}
